import mapboxgl from "mapbox-gl";

type TransformRequest = NonNullable<mapboxgl.MapboxOptions["transformRequest"]>;

const {
  VITE_APP_MAPBOX_ACCESS_TOKEN: GENERAL_MAPBOX_ACCESS_TOKEN,
  VITE_APP_MAPBOX_ACCESS_TOKEN_VISRISK: VISRISK_MAPBOX_ACCESS_TOKEN,
} = import.meta.env;

const VISRISK_MAPBOX_USERNAME = "yilab";

function getOwnersFromTilesetPath(tilesetPath: string) {
  return tilesetPath
    .split(",")
    .map((tilesetId) => tilesetId.split(".")[0])
    .filter((owner): owner is string => !!owner)
    .map(decodeURIComponent);
}

function getOwnersFromMapboxUrl(url: string) {
  const mapboxStyleMatch = url.match(/^mapbox:\/\/styles\/([^/?#]+)\//);
  if (mapboxStyleMatch) {
    return [decodeURIComponent(mapboxStyleMatch[1])];
  }

  const mapboxFontsMatch = url.match(/^mapbox:\/\/fonts\/([^/?#]+)\//);
  if (mapboxFontsMatch) {
    return [decodeURIComponent(mapboxFontsMatch[1])];
  }

  const mapboxTilesetMatch = url.match(/^mapbox:\/\/([^/?#]+)/);
  if (mapboxTilesetMatch) {
    return getOwnersFromTilesetPath(mapboxTilesetMatch[1]);
  }

  return [];
}

function getOwnersFromMapboxApiUrl(url: string) {
  try {
    const { hostname, pathname } = new URL(url);
    if (hostname !== "api.mapbox.com" && !hostname.endsWith(".mapbox.com")) {
      return [];
    }

    const styleMatch = pathname.match(/\/styles\/v1\/([^/]+)/);
    if (styleMatch) {
      return [decodeURIComponent(styleMatch[1])];
    }

    const fontsMatch = pathname.match(/\/fonts\/v1\/([^/]+)/);
    if (fontsMatch) {
      return [decodeURIComponent(fontsMatch[1])];
    }

    const tilesetMatch = pathname.match(/\/v4\/([^/]+)/);
    if (tilesetMatch) {
      return getOwnersFromTilesetPath(tilesetMatch[1]);
    }
  } catch {
    return [];
  }

  return [];
}

function isMapboxApiUrl(url: string) {
  try {
    const { hostname } = new URL(url);
    return hostname === "api.mapbox.com" || hostname.endsWith(".mapbox.com");
  } catch {
    return false;
  }
}

function getMapboxOwners(url: string) {
  if (url.startsWith("mapbox://")) {
    return getOwnersFromMapboxUrl(url);
  }

  return getOwnersFromMapboxApiUrl(url);
}

export function getMapboxAccessTokenForMapboxUrl(url: unknown) {
  if (typeof url !== "string" || !url) {
    return GENERAL_MAPBOX_ACCESS_TOKEN;
  }

  const owners = getMapboxOwners(url);

  if (owners.some((owner) => owner.toLowerCase() === VISRISK_MAPBOX_USERNAME)) {
    return VISRISK_MAPBOX_ACCESS_TOKEN || GENERAL_MAPBOX_ACCESS_TOKEN;
  }

  return GENERAL_MAPBOX_ACCESS_TOKEN;
}

function withAccessToken(url: string, accessToken: string | undefined) {
  if (!accessToken || !isMapboxApiUrl(url)) {
    return url;
  }

  const updatedUrl = new URL(url);
  updatedUrl.searchParams.set("access_token", accessToken);
  return updatedUrl.toString();
}

export function withMapboxTokenTransformRequest(
  transformRequest: mapboxgl.MapboxOptions["transformRequest"]
): TransformRequest {
  return (url, resourceType) => {
    const request = transformRequest?.(url, resourceType);
    const requestUrl = request?.url ?? url;
    const urlWithToken = withAccessToken(
      requestUrl,
      getMapboxAccessTokenForMapboxUrl(requestUrl)
    );

    if (request) {
      return {
        ...request,
        url: urlWithToken,
      };
    }

    return { url: urlWithToken };
  };
}

export function withMapboxRequestTokensMapOptions<
  T extends mapboxgl.MapboxOptions,
>(options: T): T {
  return {
    ...options,
    accessToken: getMapboxAccessTokenForMapboxUrl(
      typeof options.style === "string" ? options.style : ""
    ),
    transformRequest: withMapboxTokenTransformRequest(options.transformRequest),
  };
}
